import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Trash2, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

const claves = {
  codigo: (r) => r.codigo_parte?.toLowerCase() || "",
  nombre: (r) => r.nombre?.toLowerCase() || "",
  categoria: (r) => r.categoria?.toLowerCase() || "",
  grupo: (r) => r.grupo_equivalencia?.nombre?.toLowerCase() || null,
  modelos: (r) => (r.modelos || []).map((m) => m.nombre_mostrar || `${m.marca} ${m.modelo}`).join(", ").toLowerCase() || null,
};

const Encabezado = ({ id, label, orden, onSort, className = "" }) => (
  <TableHead className={className}>
    <button data-testid={`ordenar-${id}`} className="flex items-center gap-1 hover:text-foreground transition-colors" onClick={() => onSort(id)}>
      {label}
      {orden.key === id
        ? (orden.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
        : <ArrowUpDown size={12} className="opacity-30" />}
    </button>
  </TableHead>
);

export const CatalogoTabla = ({ repuestos, puedeEditar, puedeBorrar, puedeCrear, onEditar, onEliminar, seleccion = [], onToggleSel, onToggleTodos }) => {
  const [orden, setOrden] = useState({ key: null, dir: "asc" });
  const onSort = (key) => setOrden((o) => ({ key, dir: o.key === key && o.dir === "asc" ? "desc" : "asc" }));
  const selSet = new Set(seleccion);
  const todosSel = repuestos.length > 0 && repuestos.every((r) => selSet.has(r.id));
  const mostrarAcciones = puedeEditar || puedeBorrar;
  const colSpan = 5 + (puedeEditar ? 1 : 0) + (mostrarAcciones ? 1 : 0);

  const lista = useMemo(() => {
    if (!orden.key) return repuestos;
    const val = claves[orden.key];
    const dir = orden.dir === "asc" ? 1 : -1;
    return [...repuestos].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      return va.localeCompare(vb) * dir;
    });
  }, [repuestos, orden]);

  return (
    <div className="bg-white border border-border rounded-sm overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-secondary">
            {puedeEditar && (
              <TableHead className="w-8">
                <Checkbox data-testid="catalogo-seleccionar-todos" checked={todosSel}
                  onCheckedChange={() => onToggleTodos && onToggleTodos(repuestos.map((r) => r.id), !todosSel)} />
              </TableHead>
            )}
            <Encabezado id="codigo" label="Código" orden={orden} onSort={onSort} />
            <Encabezado id="nombre" label="Nombre" orden={orden} onSort={onSort} />
            <Encabezado id="categoria" label="Categoría" orden={orden} onSort={onSort} className="hidden md:table-cell" />
            <Encabezado id="grupo" label="Grupo de equivalencia" orden={orden} onSort={onSort} className="hidden lg:table-cell" />
            <Encabezado id="modelos" label="Modelos" orden={orden} onSort={onSort} className="hidden lg:table-cell" />
            {mostrarAcciones && <TableHead className="w-24"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {lista.length === 0 && (
            <TableRow><TableCell colSpan={colSpan} className="text-center text-muted-foreground py-10">
              El catálogo está vacío. {puedeCrear ? "Agrega el primer repuesto." : "Solicita a un administrador que cargue repuestos."}
            </TableCell></TableRow>
          )}
          {lista.map((r) => (
            <TableRow key={r.id} data-testid={`repuesto-row-${r.codigo_parte}`} data-state={selSet.has(r.id) ? "selected" : undefined}>
              {puedeEditar && (
                <TableCell className="w-8">
                  <Checkbox data-testid={`seleccionar-repuesto-${r.codigo_parte}`} checked={selSet.has(r.id)}
                    onCheckedChange={() => onToggleSel && onToggleSel(r.id)} />
                </TableCell>
              )}
              <TableCell className="font-mono text-xs font-semibold">{r.codigo_parte}</TableCell>
              <TableCell>
                <span>{r.nombre}</span>
                {r.es_plastico && (
                  <span data-testid={`plastico-repuesto-${r.codigo_parte}`}
                    className="ml-1.5 inline-block rounded-sm bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 align-middle">
                    Plástico
                  </span>
                )}
                {r.color && (
                  <span data-testid={`color-repuesto-${r.codigo_parte}`}
                    className="ml-1.5 inline-block rounded-sm bg-sky-50 border border-sky-200 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-sky-700 align-middle">
                    {r.color}
                  </span>
                )}
              </TableCell>
              <TableCell className="hidden md:table-cell text-muted-foreground">{r.categoria}</TableCell>
              <TableCell className="hidden lg:table-cell text-muted-foreground text-xs" data-testid={`grupo-repuesto-${r.codigo_parte}`}>
                {r.grupo_equivalencia?.nombre || "—"}
              </TableCell>
              <TableCell className="hidden lg:table-cell text-muted-foreground text-xs" data-testid={`modelos-repuesto-${r.codigo_parte}`}>
                {(r.modelos || []).map((m) => m.nombre_mostrar || `${m.marca} ${m.modelo}`).join(", ")}
              </TableCell>
              {mostrarAcciones && (
                <TableCell>
                  <div className="flex gap-1">
                    {puedeEditar && (
                      <Button data-testid={`editar-repuesto-${r.codigo_parte}`} variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEditar(r)}><Pencil size={14} /></Button>
                    )}
                    {puedeBorrar && (
                      <Button data-testid={`eliminar-repuesto-${r.codigo_parte}`} variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onEliminar(r.id)}><Trash2 size={14} /></Button>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
